/**
 * Validates the assignment document via `safeParse` (never throws, never watches) and turns Zod's
 * issues into dotted, document-rooted paths, e.g. `definition.screens.2.content.0.alt` or
 * `definition.exercises.1.options.0.text`. The template type picks the schema (`documentSchemaFor`);
 * no type yet reads as READING, the shape every template had before RND-196.
 */
import { computed, ref, toValue } from 'vue'

import type { MaybeRefOrGetter } from 'vue'
import type { AssessmentValidationIssue, UseValidateDefinitionReturn } from '../assessments/interfaces'
import type { ParsedAssignmentTemplateDocument } from '../../lib/assignments/schema'
import type { ParsedMathPracticeDocument } from '../../lib/assignments/math-practice-schema'

import { withCount } from '@shared/lib/format'

import { AssignmentType } from './enums'
import { documentSchemaFor } from '../../lib/assignments/schema'

/** The shared return type narrows `document` to the assessment parse output; re-declare that one field. */
interface AssignmentValidationSuccess {
  valid: true
  document: ParsedAssignmentTemplateDocument | ParsedMathPracticeDocument
  issues: AssessmentValidationIssue[]
}

interface AssignmentValidationFailure {
  valid: false
  document: null
  issues: AssessmentValidationIssue[]
}

type AssignmentValidationResult = AssignmentValidationSuccess | AssignmentValidationFailure

interface UseValidateAssignmentDefinitionReturn
  extends Omit<UseValidateDefinitionReturn, 'validate'> {
  validate: () => AssignmentValidationResult
}

const toSegments = (path: readonly PropertyKey[]): (string | number)[] =>
  path.map(segment => (typeof segment === 'number' ? segment : String(segment)))

const toIssue = (
  issue: { path: readonly PropertyKey[], message: string },
): AssessmentValidationIssue => {
  const segments = toSegments(issue.path)

  return {
    segments,
    path: segments.join('.'),
    message: issue.message,
  }
}

export const useValidateAssignmentDefinition = (
  document: MaybeRefOrGetter<unknown>,
  type: MaybeRefOrGetter<AssignmentType | null> = AssignmentType.Reading,
): UseValidateAssignmentDefinitionReturn => {
  const issues = ref<AssessmentValidationIssue[]>([])
  const hasValidated = ref<boolean>(false)

  const isValid = computed<boolean>(() => hasValidated.value && issues.value.length === 0)

  const canSave = computed<boolean>(() => isValid.value)

  const summary = computed<string | null>(() => {
    if (!hasValidated.value) {
      return null
    }
    const count = issues.value.length

    if (count === 0) {
      return 'No problems found'
    }

    return `${withCount(count, { one: 'problem', other: 'problems' })} found`
  })

  const validate = (): AssignmentValidationResult => {
    const parsed = documentSchemaFor(toValue(type) ?? AssignmentType.Reading).safeParse(toValue(document))

    hasValidated.value = true

    if (parsed.success) {
      issues.value = []
      return { valid: true, document: parsed.data, issues: [] }
    }

    issues.value = parsed.error.issues.map(toIssue)

    return { valid: false, document: null, issues: issues.value }
  }

  const issuesForPath = (path: string): AssessmentValidationIssue[] =>
    issues.value.filter(issue => issue.path === path || issue.path.startsWith(`${path}.`))

  const reset = (): void => {
    issues.value = []
    hasValidated.value = false
  }

  return {
    issues,
    hasValidated,
    isValid,
    summary,
    canSave,
    validate,
    issuesForPath,
    reset,
  }
}
